import { memo, useLayoutEffect, useRef } from "react"
import type { TimelineWaveform } from "./types"

type Props = {
  waveform: TimelineWaveform
  sourceInMs: number
  sourceOutMs: number
  widthPx: number
  heightPx?: number
  /**
   * Эффективная громкость дорожки 0..1 (mute = 0): амплитуда волны масштабируется
   * по формуле прототипа `0.26 + 0.74 * gain`, изменение анимируется.
   */
  gain?: number
  /**
   * center — зеркальная волна вокруг середины (аудио-клипы);
   * bottom — «верхняя половина» той же волны, столбики растут от нижней кромки
   * (компактная гистограмма на видеоклипах).
   */
  anchor?: "center" | "bottom"
  className?: string
}

/** Шаг столбцов на экране (CSS px). */
const BAR_STEP_PX = 2
const BAR_GAP_PX = 1
/** Тайминг анимации амплитуды — как у геометрии в прототипе (.12s ease-out). */
const GAIN_ANIM_MS = 120
/** Минимальная высота столбика — 6% высоты (прототип: max(6, ...)%). */
const MIN_BAR_RATIO = 0.06

/**
 * Canvas-waveform по серверным пикам.
 * x → время источника линейно между sourceIn/Out — trim и rate-stretch корректны.
 */
export const WaveformCanvas = memo(function WaveformCanvas({
  waveform,
  sourceInMs,
  sourceOutMs,
  widthPx,
  heightPx = 34,
  gain = 1,
  anchor = "center",
  className,
}: Props) {
  const canvasRef = useRef<HTMLCanvasElement>(null)
  const animRef = useRef<{ raf: number | null; value: number }>({ raf: null, value: gain })

  useLayoutEffect(() => {
    const canvas = canvasRef.current
    if (!canvas || widthPx <= 0 || heightPx <= 0) return

    const dpr = Math.max(1, window.devicePixelRatio || 1)
    const cssW = Math.max(1, Math.round(widthPx))
    const cssH = Math.max(1, Math.round(heightPx))
    canvas.width = Math.round(cssW * dpr)
    canvas.height = Math.round(cssH * dpr)
    canvas.style.width = `${cssW}px`
    canvas.style.height = `${cssH}px`

    const ctx = canvas.getContext("2d")
    if (!ctx) return
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0)

    const peaks = waveform.peaks
    const intervalMs = waveform.interval_ms > 0 ? waveform.interval_ms : 40
    if (peaks.length === 0) return

    const rangeMs = Math.max(1, sourceOutMs - sourceInMs)
    const midY = cssH / 2

    const draw = (g: number) => {
      ctx.clearRect(0, 0, cssW, cssH)
      // Цвет читается на каждый кадр: transition-colors на canvas анимирует и цвет волны.
      ctx.fillStyle = getComputedStyle(canvas).color || "currentColor"
      const scale = 0.26 + 0.74 * Math.max(0, Math.min(1, g))
      const minHalf = Math.max(0.5, midY * MIN_BAR_RATIO)
      for (let x = 0; x < cssW; x += BAR_STEP_PX) {
        const t = sourceInMs + ((x + BAR_STEP_PX / 2) / cssW) * rangeMs
        const idx = Math.min(peaks.length - 1, Math.max(0, Math.floor(t / intervalMs)))
        // Берём max по соседним пикам, попадающим в ширину столбца.
        const t1 = sourceInMs + ((x + BAR_STEP_PX) / cssW) * rangeMs
        const idxEnd = Math.min(peaks.length - 1, Math.max(idx, Math.floor(t1 / intervalMs)))
        let amp = 0
        for (let i = idx; i <= idxEnd; i += 1) {
          const v = peaks[i] ?? 0
          if (v > amp) amp = v
        }
        const half = Math.max(minHalf, (amp / 255) * midY * scale)
        const barW = Math.max(1, BAR_STEP_PX - BAR_GAP_PX)
        if (anchor === "bottom") {
          // Половина зеркальной волны в полный рост canvas: столбик от нижней кромки.
          const h = Math.min(cssH, half * 2)
          ctx.fillRect(x, cssH - h, barW, h)
        } else {
          ctx.fillRect(x, midY - half, barW, half * 2)
        }
      }
    }

    const anim = animRef.current
    if (anim.raf != null) cancelAnimationFrame(anim.raf)
    const from = anim.value
    if (from === gain) {
      draw(gain)
      return
    }
    const startedAt = performance.now()
    const tick = (now: number) => {
      const p = Math.min(1, (now - startedAt) / GAIN_ANIM_MS)
      const eased = 1 - (1 - p) * (1 - p)
      anim.value = from + (gain - from) * eased
      draw(anim.value)
      anim.raf = p < 1 ? requestAnimationFrame(tick) : null
    }
    anim.raf = requestAnimationFrame(tick)
    return () => {
      if (anim.raf != null) cancelAnimationFrame(anim.raf)
    }
  }, [waveform, sourceInMs, sourceOutMs, widthPx, heightPx, gain, anchor])

  return <canvas ref={canvasRef} className={className} aria-hidden width={1} height={1} />
})
